// client/src/components/plataforma/siteInstitucional/SectionsManager.js
import React, { useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Trash2, GripVertical } from 'lucide-react';

// scopeOnly: null = compartilhada (matriz+franquia+conta). Caso contrário,
// só o scope indicado pode usar/ver essa opção no dropdown — precisa bater
// com SECOES_EXCLUSIVAS_POR_SCOPE em server/controllers/institucional/HomeConfigController.js
// (aqui é só UX — o enforcement real acontece no backend).
const ALL_SECTION_TYPES = [
    { value: 'hero',                label: 'Herói Principal (Banner)',  scopeOnly: null },
    { value: 'banner',              label: 'Banner Adicional',          scopeOnly: null },
    { value: 'features',            label: 'Recursos',                  scopeOnly: null },
    { value: 'services',            label: 'Serviços',                  scopeOnly: null },
    { value: 'news',                label: 'Notícias',                  scopeOnly: null },
    { value: 'update',              label: 'Atualizações',              scopeOnly: null },
    { value: 'benefit',             label: 'Benefícios',                scopeOnly: null },
    { value: 'testimonials',        label: 'Depoimentos',               scopeOnly: null },
    { value: 'pricing_contas',      label: 'Planos de Contas',          scopeOnly: 'matriz' },
    { value: 'pricing_franquias',   label: 'Planos de Franquias',       scopeOnly: 'matriz' },
    { value: 'faq',                 label: 'Perguntas Frequentes',      scopeOnly: null },
    { value: 'cta',                 label: 'Chamada para Ação',         scopeOnly: null },
    { value: 'contact',             label: 'Formulário de Contato',     scopeOnly: null },
    { value: 'social',              label: 'Redes Sociais',             scopeOnly: null },
    { value: 'stats',               label: 'Estatísticas',              scopeOnly: null },
    { value: 'segmentos',           label: 'Segmentos de Atuação',      scopeOnly: 'conta' },
    { value: 'unidades',            label: 'Nossas Unidades',           scopeOnly: 'conta' },
    { value: 'loja',                label: 'Nossa Loja',                scopeOnly: 'conta' },
    { value: 'acessos_rapidos',     label: 'Acessos Rápidos',           scopeOnly: 'conta' },
    { value: 'categorias_vitrine',  label: 'Categorias em Destaque',    scopeOnly: 'conta' },
    { value: 'contas_por_segmento', label: 'Contas por Segmento',       scopeOnly: 'franquia' },
];

const CONTENT_LIST_TYPES = ['features', 'services', 'news', 'update', 'benefit', 'testimonials', 'faq'];
const LOJA_CATEGORIAS = [
    { value: 'servicos', label: 'Serviços' },
    { value: 'produtos', label: 'Produtos' },
    { value: 'recursos', label: 'Locações' },
    { value: 'pacotes', label: 'Pacotes' },
    { value: 'ofertas', label: 'Ofertas' },
];
const CATEGORIAS_VITRINE_TIPOS = [
    { value: 'servico', label: 'Serviços' },
    { value: 'produto', label: 'Produtos' },
    { value: 'recurso', label: 'Recursos' },
    { value: 'curso', label: 'Cursos' },
    { value: 'geral', label: 'Geral' },
];
const ACESSOS_RAPIDOS_OPCOES = [
    { value: 'agendamento', label: 'Agendamento Rápido' },
    { value: 'loja', label: 'Nossa Loja' },
    { value: 'unidades', label: 'Nossas Unidades' },
    { value: 'produtos', label: 'Nossos Produtos' },
    { value: 'cursos', label: 'Nossos Cursos' },
    { value: 'locacoes', label: 'Locações' },
];
const SEM_CONFIG_EXTRA_TYPES = ['segmentos', 'unidades', 'contas_por_segmento'];

// Valores de Banner.tipo (server/models/institucional/Banner.js) — a seção
// 'banner' escolhe UM desses tipos e só lista banners dele (evita misturar
// formatos incompatíveis num mesmo bloco). Horizontal/Vertical/Quadrado
// aceitam vários banners por seção (carrossel rotativo 3s no site/conta);
// os demais aceitam só 1.
const BANNER_TIPOS = [
    { value: 'horizontal', label: 'Banner Horizontal (Paisagem)' },
    { value: 'horizontal-fino', label: 'Banner Horizontal Fino (Faixa)' },
    { value: 'quadrado', label: 'Banner Quadrado' },
    { value: 'vertical', label: 'Banner Vertical (Celular/Story)' },
    { value: 'left-image-content', label: 'Layout: Imagem à Esquerda' },
    { value: 'right-image-content', label: 'Layout: Imagem à Direita' },
];
const MULTI_BANNER_TIPOS = ['horizontal', 'vertical', 'quadrado'];

const SectionItem = React.memo(({
    section,
    index,
    onSectionChange,
    onRemove,
    dataSources,
    bannerManagement,
    userScope
}) => {

    const handleFieldChange = (field, value) => {
        const updatedSection = { ...section };
        const configFields = ['bannerId', 'bannerIds', 'bannerTipo', 'maxItems', 'columns', 'layout', 'lojaCategorias', 'lojaQuantidades', 'acessosAtivos', 'mostrarRedesSociais', 'categoriasVitrineTipo', 'categoriasVitrineIds'];

        if (configFields.includes(field)) {
            updatedSection.config = { ...(updatedSection.config || {}), [field]: value };
        } else {
            updatedSection[field] = value;
        }
        onSectionChange(index, updatedSection);
    };

    const toggleConfigArrayValue = (field, value, defaultAll) => {
        const current = section.config?.[field] ?? defaultAll;
        const next = current.includes(value)
            ? current.filter(v => v !== value)
            : [...current, value];
        handleFieldChange(field, next);
    };

    // Tipos disponíveis: compartilhadas + as exclusivas do scope do usuário.
    // Sempre inclui o tipo atual (para não quebrar seções herdadas de outro scope).
    const availableSectionTypes = ALL_SECTION_TYPES.filter(
        t => !t.scopeOnly || t.scopeOnly === userScope || t.value === section.type
    );

    const sectionTypeInfo = ALL_SECTION_TYPES.find(t => t.value === section.type);
    
    const { banners, loading } = dataSources;
    
    const dataKeyMap = {
        features: 'features',
        services: 'services',
        news: 'news',
        update: 'update',
        benefit: 'benefit',
        testimonials: 'testimonials',
        faq: 'faqs'
    };
    const dataKey = dataKeyMap[section.type];
    const relatedData = dataSources[dataKey] || [];
    
    // ✅ FUNÇÃO PARA RENDERIZAR SELEÇÃO DE MÚLTIPLOS BANNERS
    // O tipo de banner escolhido aqui é o que define o layout renderizado no
    // site/conta (horizontal = carrossel de 1 por vez, ver
    // home-sections-component.js#renderBannerCarousel; vertical/quadrado =
    // tira lado a lado com rolagem automática, ver #renderBannerStrip) — por
    // isso o picker só lista banners desse tipo, nunca todos os ativos.
    const renderBannerMultiSelect = () => {
        if (section.type !== 'banner') return null;

        const bannerTipo = section.config?.bannerTipo || '';
        const currentBannerIds = section.config?.bannerIds || [];
        const isMulti = MULTI_BANNER_TIPOS.includes(bannerTipo);
        const bannerTipoInfo = BANNER_TIPOS.find(t => t.value === bannerTipo);

        // Trocar o tipo invalida a seleção anterior (podia ser de outro
        // tipo/formato incompatível) — mesmo padrão já usado pela seção
        // 'categorias_vitrine' logo abaixo neste arquivo.
        const handleTipoChange = (e) => {
            const updatedSection = {
                ...section,
                config: { ...(section.config || {}), bannerTipo: e.target.value || undefined, bannerIds: [] }
            };
            onSectionChange(index, updatedSection);
        };

        const bannersDoTipo = (banners || []).filter(b => b.tipo === bannerTipo);
        const alreadySelected = new Set(currentBannerIds.map(String));
        const availableBanners = bannersDoTipo.filter(b => !alreadySelected.has(String(b._id)));
        const podeAdicionarMais = isMulti || currentBannerIds.length === 0;

        const handleAddLocal = (e) => {
            const bannerId = e.target.value;
            if (!bannerId) return;
            handleFieldChange('bannerIds', [...currentBannerIds, bannerId]);
            e.target.value = '';
        };

        const handleRemoveLocal = (bannerId) => {
            handleFieldChange('bannerIds', currentBannerIds.filter(id => String(id) !== String(bannerId)));
        };

        return (
            <>
                <div className="form-group">
                    <label>Tipo de Banner</label>
                    <select value={bannerTipo} onChange={handleTipoChange} className="form-input">
                        <option value="">Selecione o tipo...</option>
                        {BANNER_TIPOS.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </select>
                    {bannerTipo && (
                        <p className="text-xs text-gray-500 mt-1">
                            {!isMulti
                                ? 'Este tipo aceita apenas um banner por seção.'
                                : bannerTipo === 'horizontal'
                                    ? 'Este tipo aceita mais de um banner — exibidos um por vez, em carrossel rotativo (3s) no site.'
                                    : 'Este tipo aceita mais de um banner — exibidos lado a lado, com rolagem automática (3s) pro que não couber na tela.'}
                        </p>
                    )}
                </div>

                {bannerTipo ? (
                    <div className="form-group md:col-span-2">
                        <label>Selecionar Banners</label>

                        <div className="space-y-2">
                            {currentBannerIds.length > 0 && (
                                <div className="bg-gray-50 p-3 rounded border">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Banners selecionados ({currentBannerIds.length}):</p>
                                    <div className="space-y-1">
                                        {currentBannerIds.map((bannerId) => {
                                            const banner = bannersDoTipo.find(b => String(b._id) === String(bannerId));
                                            return banner ? (
                                                <div key={bannerId} className="flex items-center justify-between bg-white p-2 rounded border">
                                                    <span className="text-sm">{banner.title} ({banner.tipo})</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveLocal(bannerId)}
                                                        className="text-red-600 hover:text-red-800 text-sm"
                                                    >
                                                        Remover
                                                    </button>
                                                </div>
                                            ) : null;
                                        })}
                                    </div>
                                </div>
                            )}

                            {podeAdicionarMais && availableBanners.length > 0 ? (
                                <select
                                    value=""
                                    onChange={handleAddLocal}
                                    className="form-input"
                                    disabled={loading.bannersLoading}
                                >
                                    <option value="">Selecionar banner para adicionar...</option>
                                    {availableBanners.map(banner => (
                                        <option key={banner._id} value={banner._id}>
                                            {banner.title} ({banner.tipo})
                                        </option>
                                    ))}
                                </select>
                            ) : podeAdicionarMais && currentBannerIds.length === 0 ? (
                                <p className="text-sm text-gray-500">Nenhum banner do tipo "{bannerTipoInfo?.label}" disponível. Crie um primeiro.</p>
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <div className="form-group md:col-span-2">
                        <p className="text-sm text-gray-500">Selecione o tipo de banner acima para escolher quais banners exibir.</p>
                    </div>
                )}
            </>
        );
    };

    // ✅ FUNÇÃO PARA RENDERIZAR SELEÇÃO DE BANNER ÚNICO (HERO)
    const renderSingleBannerSelect = () => {
        if (section.type !== 'hero') return null;
        
        return (
            <div className="form-group md:col-span-2">
                <label>Selecionar Banner</label>
                <select 
                    value={section.config?.bannerId || ''} 
                    onChange={(e) => handleFieldChange('bannerId', e.target.value)} 
                    className="form-input" 
                    disabled={loading.bannersLoading}
                >
                    <option value="">{loading.bannersLoading ? 'Carregando...' : 'Selecione um banner'}</option>
                    {(banners || []).map(banner => (
                        <option key={banner._id} value={banner._id}>
                            {banner.title} ({banner.tipo})
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    return (
        <div className="border rounded p-4 mb-4 bg-white shadow-sm">
            <div className="flex items-start gap-2">
                <div className="cursor-grab p-2 text-gray-500 hover:text-gray-800 mt-1">
                    <GripVertical size={20} />
                </div>
                <div className="flex-grow">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="font-medium text-gray-800">Seção {index + 1}: {sectionTypeInfo?.label}</h4>
                        <button type="button" onClick={() => onRemove(index)} className="text-red-600 hover:text-red-800">
                            <Trash2 size={16} />
                        </button>
                    </div>
                    
                    <div className="form-grid-container grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                        <div className="form-group">
                            <label>Tipo de Seção</label>
                            <select 
                                value={section.type} 
                                onChange={(e) => handleFieldChange('type', e.target.value)} 
                                className="form-input"
                            >
                                {availableSectionTypes.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>Título da Seção</label>
                            <input 
                                type="text" 
                                value={section.title || ''} 
                                onChange={(e) => handleFieldChange('title', e.target.value)} 
                                className="form-input" 
                                placeholder="Título da seção" 
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Subtítulo</label>
                            <input 
                                type="text" 
                                value={section.subtitle || ''} 
                                onChange={(e) => handleFieldChange('subtitle', e.target.value)} 
                                className="form-input" 
                                placeholder="Subtítulo (opcional)" 
                            />
                        </div>

                        {/* ✅ RENDERIZAÇÃO DE BANNERS - MULTIPLOS OU ÚNICO */}
                        {renderBannerMultiSelect()}
                        {renderSingleBannerSelect()}
                        
                        {CONTENT_LIST_TYPES.includes(section.type) && (
                            <>
                                <div className="form-group">
                                    <label>Máximo de Itens</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="50"
                                        value={section.config?.maxItems || 6}
                                        onChange={(e) => {
                                            const n = parseInt(e.target.value, 10);
                                            handleFieldChange('maxItems', Number.isNaN(n) ? 1 : Math.min(50, Math.max(1, n)));
                                        }}
                                        className="form-input"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>{sectionTypeInfo?.label} Disponíveis</label>
                                    <div className="text-sm text-gray-600 p-2 bg-gray-50 rounded">
                                        {relatedData.length} itens ativos
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {(section.type === 'pricing_contas' || section.type === 'pricing_franquias') && (
                            <div className="form-group">
                                <label>Planos</label>
                                <div className="text-sm text-gray-600 p-2 bg-gray-50 rounded">
                                    Os planos serão carregados automaticamente.
                                </div>
                            </div>
                        )}

                        {SEM_CONFIG_EXTRA_TYPES.includes(section.type) && (
                            <div className="form-group">
                                <label>{sectionTypeInfo?.label}</label>
                                <div className="text-sm text-gray-600 p-2 bg-gray-50 rounded">
                                    Os dados são carregados automaticamente da sua conta.
                                </div>
                            </div>
                        )}

                        {section.type === 'loja' && (
                            <div className="form-group md:col-span-2">
                                <label>Categorias exibidas</label>
                                <div className="flex flex-wrap gap-3 p-2 bg-gray-50 rounded">
                                    {LOJA_CATEGORIAS.map(cat => (
                                        <label key={cat.value} className="flex items-center gap-1 text-sm cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={(section.config?.lojaCategorias ?? LOJA_CATEGORIAS.map(c => c.value)).includes(cat.value)}
                                                onChange={() => toggleConfigArrayValue('lojaCategorias', cat.value, LOJA_CATEGORIAS.map(c => c.value))}
                                            />
                                            {cat.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {section.type === 'loja' && (
                            <div className="form-group md:col-span-2">
                                <label>Quantidade exibida na home (por categoria, máx. 20 — o restante fica disponível na loja completa)</label>
                                <div className="flex flex-wrap gap-4 p-2 bg-gray-50 rounded">
                                    {(section.config?.lojaCategorias ?? LOJA_CATEGORIAS.map(c => c.value))
                                        .map(catValue => LOJA_CATEGORIAS.find(c => c.value === catValue))
                                        .filter(Boolean)
                                        .map(cat => (
                                            <label key={cat.value} className="flex items-center gap-2 text-sm">
                                                {cat.label}
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={20}
                                                    className="form-input"
                                                    style={{ width: '4.5rem' }}
                                                    value={section.config?.lojaQuantidades?.[cat.value] ?? 8}
                                                    onChange={(e) => {
                                                        const valor = Math.min(20, Math.max(1, Number(e.target.value) || 1));
                                                        handleFieldChange('lojaQuantidades', {
                                                            ...(section.config?.lojaQuantidades || {}),
                                                            [cat.value]: valor
                                                        });
                                                    }}
                                                />
                                            </label>
                                        ))}
                                </div>
                            </div>
                        )}

                        {section.type === 'categorias_vitrine' && (
                            <div className="form-group">
                                <label>Tipo de Categoria</label>
                                <select
                                    value={section.config?.categoriasVitrineTipo || ''}
                                    onChange={(e) => {
                                        const updatedSection = {
                                            ...section,
                                            config: {
                                                ...(section.config || {}),
                                                categoriasVitrineTipo: e.target.value || undefined,
                                                categoriasVitrineIds: []
                                            }
                                        };
                                        onSectionChange(index, updatedSection);
                                    }}
                                    className="form-input"
                                >
                                    <option value="">Selecione o tipo...</option>
                                    {CATEGORIAS_VITRINE_TIPOS.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {section.type === 'categorias_vitrine' && section.config?.categoriasVitrineTipo && (
                            <div className="form-group md:col-span-2">
                                <label>Categorias exibidas</label>
                                {loading?.categoriasLoading ? (
                                    <div className="text-sm text-gray-600 p-2 bg-gray-50 rounded">Carregando categorias...</div>
                                ) : (
                                    <>
                                        <div className="flex flex-wrap gap-3 p-2 bg-gray-50 rounded">
                                            {(dataSources.categorias || [])
                                                .filter(cat => cat.tipo === section.config.categoriasVitrineTipo)
                                                .map(cat => {
                                                    const catId = cat._id || cat.id;
                                                    const selecionadas = section.config?.categoriasVitrineIds || [];
                                                    return (
                                                        <label key={catId} className="flex items-center gap-1 text-sm cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={selecionadas.some(id => String(id) === String(catId))}
                                                                onChange={() => {
                                                                    const jaSelecionada = selecionadas.some(id => String(id) === String(catId));
                                                                    const next = jaSelecionada
                                                                        ? selecionadas.filter(id => String(id) !== String(catId))
                                                                        : [...selecionadas, catId];
                                                                    handleFieldChange('categoriasVitrineIds', next);
                                                                }}
                                                            />
                                                            {cat.nome}{cat.categoriaPai ? ' (subcategoria)' : ''}
                                                        </label>
                                                    );
                                                })}
                                        </div>
                                        {(dataSources.categorias || []).filter(cat => cat.tipo === section.config.categoriasVitrineTipo).length === 0 && (
                                            <p className="text-sm text-gray-500 mt-1">Nenhuma categoria desse tipo cadastrada ainda.</p>
                                        )}
                                    </>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    Clicar numa categoria com subcategorias abre elas na home; categorias sem subcategoria levam direto pra loja filtrada.
                                </p>
                            </div>
                        )}

                        {section.type === 'acessos_rapidos' && (
                            <div className="form-group md:col-span-2">
                                <label>Atalhos exibidos</label>
                                <div className="flex flex-wrap gap-3 p-2 bg-gray-50 rounded">
                                    {ACESSOS_RAPIDOS_OPCOES.map(opt => (
                                        <label key={opt.value} className="flex items-center gap-1 text-sm cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={(section.config?.acessosAtivos ?? ['agendamento', 'loja', 'unidades']).includes(opt.value)}
                                                onChange={() => toggleConfigArrayValue('acessosAtivos', opt.value, ['agendamento', 'loja', 'unidades'])}
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {section.type === 'contact' && (
                            <div className="form-group md:col-span-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!section.config?.mostrarRedesSociais}
                                        onChange={(e) => handleFieldChange('mostrarRedesSociais', e.target.checked)}
                                    />
                                    Mostrar redes sociais abaixo do formulário
                                </label>
                                <p className="text-xs text-gray-500 mt-1">
                                    Usa as redes cadastradas em Site Institucional → Redes Sociais (não altera as que já aparecem no rodapé).
                                </p>
                            </div>
                        )}

                        <div className="form-group flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer h-full">
                                <input
                                    type="checkbox"
                                    checked={!!section.enabled}
                                    onChange={(e) => handleFieldChange('enabled', e.target.checked)}
                                    className="h-4 w-4 rounded"
                                /> 
                                Seção Ativa
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

const SectionsManager = ({
    sections,
    onChange,
    dataSources,
    bannerManagement,
    userScope
}) => {
    const handleSectionChange = useCallback((index, updatedSection) => {
        const newSections = [...(sections || [])];
        newSections[index] = updatedSection;
        onChange(newSections);
    }, [sections, onChange]);

    const addSection = useCallback(() => {
        const newSection = { 
            type: 'features', 
            order: (sections?.length || 0) + 1, 
            enabled: true, 
            title: 'Nova Seção', 
            subtitle: '', 
            config: { maxItems: 6 } 
        };
        onChange([...(sections || []), newSection]);
    }, [sections, onChange]);

    const removeSection = useCallback((indexToRemove) => {
        const updatedSections = (sections || []).filter((_, i) => i !== indexToRemove).map((s, newIndex) => ({ ...s, order: newIndex + 1 }));
        onChange(updatedSections);
    }, [sections, onChange]);

    const onDragEnd = useCallback((result) => {
        if (!result.destination) return;
        const items = Array.from(sections || []);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        const updatedItems = items.map((item, index) => ({ ...item, order: index + 1 }));
        onChange(updatedItems);
    }, [sections, onChange]);
    
    return (
        <div className="form-section mt-8">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="form-section-title">🧱 Ordem das Seções da Home</h3>
                    <p className="text-gray-600 text-sm">Arraste e solte para reordenar</p>
                </div>
                <button type="button" onClick={addSection} className="action-button action-button-secondary">
                    <Plus size={16} className="icon" /> Adicionar Seção
                </button>
            </div>
            
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="sections">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {(sections ?? []).map((section, index) => (
                                <Draggable key={`section-${index}`} draggableId={`section-${index}`} index={index}>
                                    {(provided) => (
                                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                            <SectionItem
                                                section={section}
                                                index={index}
                                                onSectionChange={handleSectionChange}
                                                onRemove={removeSection}
                                                dataSources={dataSources}
                                                bannerManagement={bannerManagement}
                                                userScope={userScope}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
};

export default SectionsManager;